import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import usePageTitle from './hooks/usePageTitle';
import Modal from './components/layout/Modal';

import ProtectedRoute from './components/auth/ProtectedRoute';
import WebLayout from './components/layout/WebLayout';
import { useAuth } from './context/AuthContext';
import { useModal } from './context/ModalContext';
// import CMSLayout from './components/layout/CMSLayout';

// Web Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyOtp from './pages/auth/VerifyOtp';
import EmailTest from './pages/auth/EmailTest';

const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const ClientLayout = lazy(() => import('./components/layout/ClientLayout'));
const NotFound = lazy(() => import('./pages/NotFound'));

//const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Users = lazy(() => import('./pages/admin/Users'));
const Clients = lazy(() => import('./pages/admin/Clients'));
const Profile = lazy(() => import('./pages/admin/Profile'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const Table = lazy(() => import('./pages/admin/Table'));
const TableFilters = lazy(() => import('./pages/admin/TableFilters'));
const TableTab = lazy(() => import('./pages/admin/TableTab'));
const TableParent = lazy(() => import('./pages/admin/TableParent'));
const TableMultiple = lazy(() => import('./pages/admin/TableMultiple'));
const TableCheckbox = lazy(() => import('./pages/admin/TableCheckbox'));
const TableDataGrid = lazy(() => import('./pages/admin/TableDataGrid'));
const SubscriptionPlans = lazy(() => import('./pages/admin/SubscriptionPlans'));

const Buttons = lazy(() => import('./pages/admin/Buttons'));
const Pagelet = lazy(() => import('./pages/admin/Pagelet'));
const Icons = lazy(() => import('./pages/admin/Icons'));
const Loading = lazy(() => import('./pages/admin/Loading'));
const Notifications = lazy(() => import('./pages/admin/Notifications'));
const Tooltips = lazy(() => import('./pages/admin/Tooltips'));

const FormLayouts = lazy(() => import('./pages/admin/FormLayouts'));
const FormValidations = lazy(() => import('./pages/admin/FormValidations'));
const FormVariations = lazy(() => import('./pages/admin/FormVariations'));

const Pending = lazy(() => import('./pages/admin/Pending'));
const CMSLayout = lazy(() => import('./components/layout/CMSLayout'));
const CmsPage = lazy(() => import('./pages/web/CmsPage'));

const Careers = lazy(() => import('./pages/web/Careers'));
const ClientWebMaker = lazy(() => import('./pages/client/WebMaker'));
const ClientCMSEditor = lazy(() => import('./pages/client/CMSEditor'));

const PageTitleManager = () => { usePageTitle(); return null; };

const FullPageSpinner = () => {
    const { user, isDarkMode, isPremium } = useAuth();
    let containerClasses = 'page-loading-case';

    if (user) {
        containerClasses += ' admin-body';
        if (isDarkMode) containerClasses += ' dark-mode';
        if (isPremium) containerClasses += ' premium-theme';
    }

    return (
        <div className={containerClasses}>
            <div className='page-loading'>
                <div className='loader-line-scale'><div></div><div></div><div></div><div></div><div></div></div>
                <h3>Loading Page...</h3><p>Please wait while we load the page.</p>
            </div>
        </div>
    );
};

const Dashboard = lazy(() => import('./pages/admin/Dashboard'));

function App() {

    //jeric - set loading delay
    {/*  
    const [isDesigningLoader, setIsDesigningLoader] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setIsDesigningLoader(false), 5000);
        return () => clearTimeout(timer);
    }, []);
    if (isDesigningLoader) {
        return <FullPageSpinner />;
    }
    */}

    const { showAlert } = useModal();

    useEffect(() => {
        const handleRateLimitError = (event) => {
            const { retryAfter } = event.detail || {};
            let content = "You have made too many requests in a short period of time. Please wait a moment before trying again.";
            if (retryAfter) {
                content = `You have made too many requests. Please wait ${retryAfter} seconds before trying again.`;
            }
            showAlert({
                style: 'warning',
                title: 'Too Many Requests',
                content: content,
                closeOnBackdropClick: false
            });
        };

        window.addEventListener('api-429-error', handleRateLimitError);
        return () => window.removeEventListener('api-429-error', handleRateLimitError);
    }, [showAlert]);


    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PageTitleManager />
            <Suspense fallback={<FullPageSpinner />}>
                <Routes>
                    <Route element={<WebLayout />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/verify-otp" element={<VerifyOtp />} />
                        <Route path="/email-test" element={<EmailTest />} />

                        <Route path="/" element={<CmsPage />} />
                        <Route path="/:slug" element={<CmsPage />} />
                        <Route path="/:slug/:recordId" element={<CmsPage />} />
                        <Route path="/:slug/:recordId/:nodeId" element={<CmsPage />} />
                        
                        {/* Hybrid Routing for Local Development */}
                        <Route path="/projects/:projectSlug" element={<CmsPage />} />
                        <Route path="/projects/:projectSlug/:slug" element={<CmsPage />} />
                        <Route path="/projects/:projectSlug/:slug/:recordId" element={<CmsPage />} />

                        <Route path="/p/:slug" element={<CmsPage />} />
                        <Route path="/p/:slug/:recordId" element={<CmsPage />} />

                        <Route path="/careers" element={<Careers />} />
                        <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<Dashboard />} />
                            <Route path="users" element={<Users />} />
                            <Route path="clients" element={<Clients />} />
                            <Route path="subscription-plans" element={<SubscriptionPlans />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="table" element={<Table />} />
                            <Route path="table-filter" element={<TableFilters />} />
                            <Route path="table-tab/*" element={<TableTab />} />
                            <Route path="table-parent/*" element={<TableParent />} />
                            <Route path="table-multiple" element={<TableMultiple />} />
                            <Route path="table-checkbox" element={<TableCheckbox />} />
                            <Route path="table-datagrid" element={<TableDataGrid />} />

                            <Route path="buttons" element={<Buttons />} />
                            <Route path="pagelet" element={<Pagelet />} />
                            <Route path="icons" element={<Icons />} />
                            <Route path="loading" element={<Loading />} />
                            <Route path="notifications" element={<Notifications />} />
                            <Route path="tooltips" element={<Tooltips />} />

                            <Route path="form-layouts" element={<FormLayouts />} />
                            <Route path="form-validations" element={<FormValidations />} />
                            <Route path="form-variations" element={<FormVariations />} />
                            <Route path="pending" element={<Pending />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Route>

                    {/* 
                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                        <Route path="/cms" element={<CMSLayout />}>
                            <Route index element={<DashboardCms />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="page/:page_name" element={<ConsoleEditor />} />
                        </Route>
                    </Route> 
                    */}

                    <Route element={<ProtectedRoute allowedRoles={['employee']} />}>
                        <Route path="/employee" element={<AdminLayout />}>
                            <Route index element={<Dashboard />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Route>
                    <Route element={<ProtectedRoute allowedRoles={['client']} />}>
                        <Route path="/client" element={<ClientLayout />}>
                            <Route index element={<Dashboard />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="web-maker" element={<ClientWebMaker />} />
                            <Route path="buttons" element={<Buttons />} />
                            <Route path="pagelet" element={<Pagelet />} />
                            <Route path="icons" element={<Icons />} />
                            <Route path="loading" element={<Loading />} />
                            <Route path="notifications" element={<Notifications />} />
                            <Route path="tooltips" element={<Tooltips />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                        <Route path="/client/web-maker/:projectId/*" element={<ClientCMSEditor />} />
                    </Route>



                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                        <Route path="/cms" element={<CMSLayout />}>
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Route>
                </Routes>
            </Suspense>
            <Modal />
        </Router>);
}

export default App;